import { helpers } from '@vuelidate/validators'
import { useNuxtApp, useRoute, useRouter } from 'nuxt/app'
import type { Validation } from '@vuelidate/core'
import useVuelidate from '@vuelidate/core'
import { reactive } from 'vue'
import type { BaseModel } from '../models/BaseModel'
import { useI18nValidators } from '../utils/validations/i18n-validators'
import { registerFormService, unregisterFormService } from './form-service-tracker.service'

export class FormService {
    private readonly $translate: any
    private readonly $route: any
    readonly $router: any
    entity: BaseModel<any> | null
    fetchData: Record<string, string[]> = {}
    data: Record<string, string[]> = reactive({})
    validationRules: Record<string, string[]> = {}
    validation: Validation | null = null
    translationName: string | null = null
    loading: boolean = false
    saving: boolean = false
    editMode: boolean = false
    currentKey: string | number | null = null

    constructor(entity: BaseModel<any> | null = null, currentKey: string | number | null = null) {
        const { $translate } = useNuxtApp()
        this.$translate = $translate
        this.$route = useRoute()
        this.$router = useRouter()
        this.currentKey = currentKey
        this.entity = entity
        registerFormService(this)
    }

    translate(toTranslate: string): string {
        return this.$translate(`${this.translationName}.${toTranslate}`)
    }

    getVuelidateRules(metaRules: any): { data: Record<string, any> } {
        const validators = useI18nValidators()
        const rawTree: any = {} // L'arbre global qui va tout contenir

        if (metaRules) {
            let keys = Object.keys(metaRules)
            keys = keys.filter(key => !metaRules[key].includes('noFrontValidation'))

            keys.forEach((key) => {
                const ruleList = metaRules[key]
                const path = key.split('.')
                const validationObject: any = {}

                ruleList.forEach((ruleKey: string) => {
                    let validatorKey = ruleKey
                    if (ruleKey.includes('regex:'))
                        validatorKey = 'regex'
                    if (ruleKey.startsWith('min:'))
                        validatorKey = (ruleList.includes('numeric') || ruleList.includes('integer')) ? 'minValue' : 'minLength'
                    if (ruleKey.startsWith('max:'))
                        validatorKey = (ruleList.includes('numeric') || ruleList.includes('integer')) ? 'maxValue' : 'maxLength'
                    if (ruleKey.startsWith('extensions:'))
                        validatorKey = 'extensions'

                    if (!validators[validatorKey])
                        return

                    if (validatorKey === 'regex') {
                        const pattern = ruleKey.split('regex:')[1]
                        const lastSlash = pattern.lastIndexOf('/')
                        try {
                            const regex = new RegExp(pattern.slice(1, lastSlash), pattern.slice(lastSlash + 1))
                            validationObject[validatorKey] = {
                                $validator: helpers.regex(regex),
                                $message: this.$translate?.(`forms.validations.${validatorKey}`) ?? 'Format invalide',
                            }
                        }
                        catch (e: any) {
                            console.warn('Invalid regex:', e.message)
                        }
                    }
                    else if (['minLength', 'maxLength', 'minValue', 'maxValue'].includes(validatorKey)) {
                        const value = Number(ruleKey.split(':')[1])
                        validationObject[validatorKey] = {
                            ...validators[validatorKey].$validator(value),
                            $message: this.$translate?.(`forms.validations.${validatorKey}`, { value }) ?? `${validatorKey} ${value}`,
                        }
                    }
                    else if (validatorKey === 'extensions') {
                        const extList = ruleKey.split('extensions:')[1]
                        validationObject[validatorKey] = validators.extensions(extList)
                    }
                    else {
                        validationObject[validatorKey] = validators[validatorKey]
                    }
                })

                // On assigne TOUT dans rawTree avec le chemin exact (y compris les '*')
                if (Object.keys(validationObject).length > 0) {
                    this._assignNestedKey(rawTree, path, validationObject)
                }
            })
        }

        const transformWildcards = (tree: any): any => {
            if (!tree || typeof tree !== 'object') return tree

            if (tree.$validator || typeof tree === 'function') return tree

            const result: any = {}

            for (const k in tree) {
                if (k === '*')
                    continue

                if (tree[k] && typeof tree[k] === 'object' && tree[k]['*']) {
                    const { '*': wildcardRules, ...arrayDirectRules } = tree[k]

                    result[k] = {
                        ...transformWildcards(arrayDirectRules),
                        $each: helpers.forEach(transformWildcards(tree[k]['*']))
                    }
                } else {
                    result[k] = transformWildcards(tree[k])
                }
            }
            return result
        }

        return { data: transformWildcards(rawTree) }
    }

    _assignNestedKey(obj: any, path: string[], value: any): void {
        let current = obj
        for (let i = 0; i < path.length; i++) {
            const key = path[i]
            if (i === path.length - 1) {
                current[key] = value
            }
            else {
                if (!current[key] || typeof current[key] !== 'object') {
                    current[key] = {}
                }
                current = current[key]
            }
        }
    }

    async init(): Promise<void> {
        if (this.currentKey)
            await this.getEntityData()
        else
            await this.getNewEntityData()
    }

    setEditMode(value: boolean): boolean {
        this.editMode = value
        return this.editMode
    }

    async getEntityData(): Promise<void> {
        this.loading = true
        const result = await this.entity.get()
        this.setData(result)
    }

    async getNewEntityData(query: { [key: string]: any } = {}): Promise<void> {
        this.loading = true
        const result = await this.entity.getCreate(query)
        this.setData(result)
    }

    setData(result: BaseModel<any>): void {
        Object.assign(this.entity, result)
        this.translationName = result.translationKey
        Object.assign(this.fetchData, result.getEditableDataFormat())
        Object.assign(this.data, reactive({ ...this.fetchData }))
        Object.assign(this.validationRules, result.getRules())
        this.loading = false
        this.setValidation()
    }

    updateData(dataKey: string, newValue: any, fullDataKey = false): any {
        if (!dataKey)
            return

        // handle autocomplete with _id
        // update {field}_id data when exist in data if newvalue.id exist
        if (newValue?.id !== undefined && this.data[`${dataKey}_id`] !== undefined) {
            this.data[`${dataKey}_id`] = newValue.id
        }

        const keys = fullDataKey ? [dataKey] : dataKey.split('.')
        let target = this.data

        for (let i = 0; i < keys.length; i++) {
            const rawKey = keys[i]
            const key = isFinite(Number(rawKey)) ? Number(rawKey) : rawKey

            if (i === keys.length - 1) {
                target[key] = newValue
                return newValue
            }

            // Prepare next level
            if (
                target[key] === undefined
                || target[key] === null
                || (typeof target[key] !== 'object' && !Array.isArray(target[key]))
            ) {
                // Decide whether next key is a number (array) or string (object)
                const nextKey = keys[i + 1]
                target[key] = Number.isFinite(Number(nextKey)) ? [] : {}
            }

            target = target[key]
        }
    }

    getDataInput(dataKey: string, fullDataKey = false): any {
        if (!dataKey)
            return null

        const keys = fullDataKey ? [dataKey] : dataKey.split('.')
        let target = this.data

        for (const rawKey of keys) {
            const key = isFinite(Number(rawKey)) ? Number(rawKey) : rawKey

            if (target === null || typeof target !== 'object' || !(key in target)) {
                return undefined
            }

            target = target[key]
        }

        return target
    }

    setValidation(): void {
        if (!this.validation)
            this.setNewValidation()
        else
            this.validation.$reset()
    }

    setNewValidation(): void {
        const vuelidateRules = reactive(this.getVuelidateRules(this.validationRules))
        this.validation = useVuelidate(vuelidateRules.data, this.data) as unknown as Validation
    }

    getValidation(dataKey: string, fullDataKey = false): Validation | undefined {
        if (!dataKey || !this.validation)
            return undefined

        const keys = fullDataKey ? [dataKey] : dataKey.split('.')
        let target: any = this.validation?.value ?? this.validation

        for (const rawKey of keys) {
            const key = isFinite(Number(rawKey)) ? Number(rawKey) : rawKey

            if (target && key in target) {
                target = target[key]
            }
            else {
                return undefined
            }
        }

        return target as Validation
    }

    async submitForm(form: HTMLFormElement): Promise<BaseModel<any>> {
        const formData = new FormData(form)
        let hasFile = false
        formData.forEach((value) => {
            if (value instanceof File)
                hasFile = true
        })

        const entity: BaseModel<any> | null = await this.submitData(hasFile ? formData : null)
        if (!entity) {
            this.saving = false
        }
        return entity
    }

    async submitData(formData: FormData | null = null): Promise<BaseModel<any>> {
        let dataToSend = formData ?? { ...this.data }
        if (dataToSend instanceof FormData)
            dataToSend = this.cleanFormData(formData)
        else
            dataToSend = this.cleanData(dataToSend)

        const isDataValid = await this.validation.$validate()
        if (!isDataValid)
            throw this.validation.$errors

        this.saving = true
        if (isDataValid) {
            if (this.entity.getKey()) {
                const res = await this.entity.put(dataToSend)
                if (res) {
                    this.setData(res)
                    this.setNewValidation()
                }
                else {
                    this.saving = false
                    return res
                }
            }
            else if (isDataValid && !this.currentKey) {
                const res = await this.entity.post(dataToSend)
                this.saving = false
                return res
            }
        }

        this.validation.$reset()

        this.saving = false
        return this.entity
    }

    cleanData(dataToEdit: Record<string, string[]>): Record<string, string[]> {
        const fileFieldsToRemove = []
        Object.keys(dataToEdit).forEach((key, value) => {
            const rules = this.entity.getRulesForProperty(key)
            if (rules.readonly)
                delete dataToEdit[key]

            // Remove file or files
            const inputType = this.entity.getInputComponentType(key)
            const file = inputType === 'file' ? dataToEdit[key] : null
            if (file) {
                fileFieldsToRemove.push(key)
                delete dataToEdit[key]
            }
        })
        this.removeValidationKeys(fileFieldsToRemove)
        this.setNewValidation()
        return dataToEdit
    }

    removeValidationKeys(keysToRemove: string[]) {
        keysToRemove.forEach((key) => {
            delete this.validationRules[key]
        })
    }

    cleanFormData(dataToEdit: FormData): FormData {
        const cleanedFormData = new FormData()
        const fieldsToRemove = []
        this._addDateFieldsToFormData(dataToEdit)

        dataToEdit.forEach((value, key) => {
            const inputType = this.entity.getInputComponentType(key)
            const formServiceDataValue = this.data[key] ?? null
            const isRequired = !!this.validationRules[key]?.find(r => r === 'required')

            // If it's a multiple select field and the value is an array
            if (['select', 'array'].includes(inputType) && Array.isArray(formServiceDataValue)) {
                const onlyDate = formServiceDataValue.length && formServiceDataValue.every((item: any) => item instanceof Date)
                if (!onlyDate) {
                    formServiceDataValue.forEach((item) => {
                        cleanedFormData.append(`${key}[]`, item)
                    })
                }
            }
            // Handle multiple file fields
            else if (['file', 'image'].includes(inputType) && Array.isArray(formServiceDataValue)) {
                formServiceDataValue.forEach((item, index) => {
                    if (item?.file instanceof File && item.file.size > 0)
                        cleanedFormData.append(`${key}[${index}][file]`, item.file)
                    else
                        fieldsToRemove.push(key)
                })
            }
            else if (['file', 'image'].includes(inputType)) {
                // If the value is a File or a Blob, it's a file field
                if (value instanceof File || value instanceof Blob || value instanceof Image) {
                    if (value.size > 0 || isRequired)
                        cleanedFormData.append(key, value)
                    else
                        fieldsToRemove.push(key)
                }
            }
            else if (typeof value === 'object' && value !== null) {
                // Stringify the object into JSON
                cleanedFormData.append(key, JSON.stringify(value))
            }
            else {
                cleanedFormData.append(key, value)
            }
        })

        this.removeValidationKeys(fieldsToRemove)
        this.setNewValidation()
        return cleanedFormData
    }

    _addDateFieldsToFormData(dataToEdit: FormData): FormData {
        Object.entries(this.data).forEach(([key, value]) => {
            if (!dataToEdit.has(key) && value instanceof Date) {
                dataToEdit.append(key, value?.toISOString())
            }
            else if (!dataToEdit.has(key) && Array.isArray(value)) {
                const onlyDate = value.every((item: any) => item instanceof Date)
                if (onlyDate) {
                    value.forEach((childValue: Date, index: number) => {
                        dataToEdit.append(`${key}[]`, childValue.toISOString())
                    })
                }
            }
        })
        return dataToEdit
    }

    resetData(): void {
        this.data = reactive({ ...this.fetchData })
        this.validation.$reset()
    }

    async deleteEntity(): Promise<BaseModel<any> | void> {
        return await this.entity.delete()
    }

    destroy(): void {
        unregisterFormService(this)
    }
}

export type FormServiceType = InstanceType<typeof FormService>
